import { useEffect, useState } from "react";
import type { DateRange } from "react-day-picker";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getHolidays } from "@/lib/collections/holidays/holidayService";
import type { Holiday } from "@/lib/collections/holidays/types";
import { calculateRequest, localDateToISO } from "@/lib/collections/vacations/vacationBalanceService";
import { VACATION_CONFIG } from "@/lib/collections/vacations/lib/vacationConfig";
import { createVacationRequest } from "@/lib/collections/vacations/vacationsService";


type Props = {
    userId: string;
    canton: string | null;
    availableDays: number; // saldo menos lo pendiente
    onCreated: () => void;
};

const thisYear = new Date().getFullYear();
const YEARS = [thisYear, thisYear + 1]; // solo se puede pedir dentro de estos años
const LAST_SELECTABLE = new Date(thisYear + 1, 11, 31);

const parse = (iso: string) => new Date(iso + "T00:00:00");
const sameCanton = (a?: string | null, b?: string | null) =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

export default function VacationRequestForm({ userId, canton, availableDays, onCreated }: Props) {
    const [range, setRange] = useState<DateRange | undefined>();
    const [reason, setReason] = useState("");
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [loadingHolidays, setLoadingHolidays] = useState(true);
    const [holidaysError, setHolidaysError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        getHolidays(YEARS)
            .then((data) => {
                if (!cancelled) setHolidays(data);
            })
            .catch(() => {
                if (!cancelled) setHolidaysError(true);
            })
            .finally(() => {
                if (!cancelled) setLoadingHolidays(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = range?.from ? localDateToISO(range.from) : null;
    const end = range?.to ? localDateToISO(range.to) : start;

    // Feriados que le aplican a este empleado: nacionales + los de su cantón
    const applicable = holidays.filter((h) => !h.canton || sameCanton(h.canton, canton));

    const yearsInRange =
        start && end ? Array.from(new Set([Number(start.slice(0, 4)), Number(end.slice(0, 4))])) : [];
    const missingYears = loadingHolidays
        ? []
        : yearsInRange.filter((y) => !holidays.some((h) => h.year === y));

    const result =
        start && end && canton ? calculateRequest({ start, end, canton, holidays }) : null;

    const exceeds = !!result?.ok && result.daysToDeduct > availableDays;

    const canSubmit =
        !!result?.ok &&
        !exceeds &&
        missingYears.length === 0 &&
        !loadingHolidays &&
        !holidaysError &&
        !saving;

    const submit = async () => {
        if (!start || !end || !result?.ok) return;
        setSaving(true);
        setError(null);
        try {
            await createVacationRequest({
                userId,
                startDate: start,
                endDate: end,
                calendarDays: result.calendarDays,
                daysDeducted: result.daysToDeduct,
                reason,
            });
            setRange(undefined);
            setReason("");
            onCreated();
        } catch (e) {
            console.error(e);
            setError(
                e instanceof Error && e.message === "OVERLAP"
                    ? "Ya tienes una solicitud pendiente o aprobada que se cruza con esas fechas."
                    : "No se pudo enviar la solicitud."
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>Pedir vacaciones</CardTitle>
                <p className="text-sm text-muted-foreground">
                    Puedes pedir hasta {availableDays} {availableDays === 1 ? "día" : "días"} (ya
                    descontando lo que tienes pendiente de aprobación).
                </p>
            </CardHeader>
            <CardContent className="space-y-4">
                {!canton && (
                    <Alert variant="destructive">
                        <AlertDescription>
                            Tu sucursal no está configurada, así que no se pueden calcular los feriados.
                            Pide a RRHH que la asigne.
                        </AlertDescription>
                    </Alert>
                )}
                {holidaysError && (
                    <Alert variant="destructive">
                        <AlertDescription>No se pudieron cargar los feriados.</AlertDescription>
                    </Alert>
                )}

                <div className="flex justify-center">
                    <Calendar
                        mode="range"
                        selected={range}
                        onSelect={setRange}
                        disabled={[{ before: today }, { after: LAST_SELECTABLE }]}
                        modifiers={{ holiday: applicable.map((h) => parse(h.date)) }}
                        modifiersClassNames={{ holiday: "text-red-600 font-semibold" }}
                    />
                </div>
                <p className="text-center text-xs text-muted-foreground">
                    Los feriados que te aplican aparecen en rojo. Para un solo día, toca la misma fecha dos
                    veces.
                </p>

                {result && (
                    <div className="space-y-2 rounded-lg border p-3 text-sm">
                        {result.ok ? (
                            <>
                                <p>
                                    <span className="font-medium">{result.calendarDays}</span> días
                                    calendario
                                    {result.holidaysInRange.length > 0 &&
                                        `, con ${result.holidaysInRange.length} feriado(s)`}
                                </p>
                                {result.holidaysInRange.length > 0 && (
                                    <ul className="list-disc pl-5 text-muted-foreground">
                                        {result.holidaysInRange.map((h) => (
                                            <li key={h.$id ?? h.date}>
                                                {h.date}: {h.name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <p className="font-semibold">
                                    Se descontarán {result.daysToDeduct} días de tu saldo
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {VACATION_CONFIG.holidaysCountInContinuousPeriod
                                        ? "Los fines de semana y feriados dentro del período cuentan como días de vacación."
                                        : "Los feriados dentro del período no se descuentan; los fines de semana sí."}
                                </p>
                            </>
                        ) : (
                            <p className="text-destructive">{result.error}</p>
                        )}
                    </div>
                )}

                {exceeds && (
                    <Alert variant="destructive">
                        <AlertDescription>
                            La solicitud supera los días que puedes pedir ({availableDays}).
                        </AlertDescription>
                    </Alert>
                )}
                {missingYears.length > 0 && (
                    <Alert variant="destructive">
                        <AlertDescription>
                            RRHH aún no cargó los feriados de {missingYears.join(", ")}. No se puede
                            calcular la solicitud con exactitud.
                        </AlertDescription>
                    </Alert>
                )}

                <div className="space-y-2">
                    <Label htmlFor="vacation-reason">Motivo (opcional)</Label>
                    <Textarea
                        id="vacation-reason"
                        value={reason}
                        maxLength={500}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </div>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <Button className="w-full" disabled={!canSubmit} onClick={submit}>
                    {saving ? "Enviando..." : "Enviar solicitud"}
                </Button>
            </CardContent>
        </Card>
    );
}